import { lazy } from "react";
import { CreditCard } from "lucide-react";
import type { AdminModule } from "../types";

const Overview = lazy(() => import("./pages/Overview").then((m) => ({ default: m.Overview })));
const Subscriptions = lazy(() =>
  import("./pages/Subscriptions").then((m) => ({ default: m.Subscriptions })),
);
const Dunning = lazy(() => import("./pages/Dunning").then((m) => ({ default: m.Dunning })));
const Plans = lazy(() => import("./pages/Plans").then((m) => ({ default: m.Plans })));
const Coupons = lazy(() => import("./pages/Coupons").then((m) => ({ default: m.Coupons })));
const Refunds = lazy(() => import("./pages/Refunds").then((m) => ({ default: m.Refunds })));

/**
 * Billing and Pricing were two sidebar entries for one job: money. What a
 * tenant is billed and what we sell are read together and changed together —
 * a tier migration on an account is a Billing action against a Pricing row —
 * so they are one module with five tabs.
 *
 * The two halves keep their own permissions. `subscription.read` governs what
 * tenants owe; `plans.manage` governs the catalogue. A finance viewer holding
 * only one of them sees only that half, and lands on it.
 */
export const revenueModule: AdminModule = {
  key: "revenue",
  label: "Revenue",
  description: "MRR, subscriptions, failed payments, and the plans they bill against.",
  group: "revenue",
  icon: CreditCard,
  routes: [
    {
      path: "overview",
      label: "Overview",
      element: <Overview />,
      requiredPermission: "admin.billing.subscription.read",
    },
    {
      path: "subscriptions",
      label: "Subscriptions",
      element: <Subscriptions />,
      requiredPermission: "admin.billing.subscription.read",
    },
    {
      path: "dunning",
      label: "Dunning",
      element: <Dunning />,
      requiredPermission: "admin.billing.subscription.read",
    },
    {
      path: "plans",
      label: "Plans",
      element: <Plans />,
      requiredPermission: "admin.billing.plans.manage",
    },
    {
      path: "coupons",
      label: "Coupons",
      element: <Coupons />,
      requiredPermission: "admin.billing.plans.manage",
    },
    // Its own ability, deliberately: reading what a tenant owes and moving
    // money back to them are different levels of trust.
    {
      path: "refunds",
      label: "Refunds",
      element: <Refunds />,
      requiredPermission: "admin.billing.refund",
    },
  ],
};
